import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api";
import { ConversationAttachmentButton } from "@/components/qorum/ConversationAttachmentButton";

type Bubble = {
  id: string;
  role: "imani" | "user";
  text: string;
};

type AnswerResponse = {
  message?: string;
  brief_ready?: boolean;
  final_brief?: string;
  turn?: number;
  // ignored on UI: profile_patch, provider
};

type Props = {
  sessionId: string;
  initialQuestion: string;
  initialImaniMessage: string;
  initialAttachmentCount?: number;
  mode?: string;
  branch?: "business" | "tendances" | "audit" | "verif";
  onConvened: () => void;
  onError: (err: unknown) => void;
};

let bubbleSeq = 0;
const nextId = () => `b_${Date.now()}_${++bubbleSeq}`;

export function MaieuteChat({
  sessionId,
  initialQuestion,
  initialImaniMessage,
  initialAttachmentCount = 0,
  mode,
  branch,
  onConvened,
  onError,
}: Props) {
  const [bubbles, setBubbles] = useState<Bubble[]>(() => {
    const init: Bubble[] = [{ id: nextId(), role: "user", text: initialQuestion }];
    const first = (initialImaniMessage ?? "").trim();
    if (first) init.push({ id: nextId(), role: "imani", text: first });
    return init;
  });
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [briefReady, setBriefReady] = useState(false);
  const [finalBrief, setFinalBrief] = useState<string | null>(null);
  const [convening, setConvening] = useState(false);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-scroll on new bubbles / thinking state
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [bubbles, thinking, briefReady]);

  const callAnswer = async (message: string) => {
    setThinking(true);
    try {
      const res = await apiFetch<AnswerResponse>(
        `/api/maieutique/${encodeURIComponent(sessionId)}/answer`,
        {
          method: "POST",
          body: JSON.stringify({ message }),
        },
      );
      const text = (res?.message ?? "").trim();
      if (text) {
        setBubbles((b) => [...b, { id: nextId(), role: "imani", text }]);
      }
      if (res?.brief_ready && res.final_brief) {
        setFinalBrief(res.final_brief);
        setBriefReady(true);
      }
    } catch (e) {
      onError(e);
    } finally {
      setThinking(false);
    }
  };


  // Refocus textarea after each Imani reply
  useEffect(() => {
    if (!thinking && !briefReady) textareaRef.current?.focus();
  }, [thinking, briefReady]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || thinking || briefReady) return;
    setBubbles((b) => [...b, { id: nextId(), role: "user", text: trimmed }]);
    setInput("");
    await callAnswer(trimmed);
  };

  const handleConvene = async () => {
    if (!finalBrief || convening) return;
    setConvening(true);
    try {
      const body: Record<string, unknown> = { final_brief: finalBrief };
      if (mode) body.mode = mode;
      if (branch === "tendances") body.branch = "tendances";
      else if (branch === "audit") body.branch = "audit";
      else if (branch === "verif") { body.branch = "verif"; body.mode = "verif"; }
      await apiFetch(
        `/api/maieutique/${encodeURIComponent(sessionId)}/convene`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );
      onConvened();
    } catch (e) {
      setConvening(false);
      onError(e);
    }
  };

  return (
    <div className="maieute-chat">
      <style>{`
        .maieute-chat {
          width: 100%;
          max-width: 720px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          height: calc(100vh - 80px);
          padding: 24px 20px 0;
          box-sizing: border-box;
        }
        .maieute-header {
          text-align: center;
          padding: 8px 0 18px;
          border-bottom: 0.5px solid rgba(255,23,68, 0.18);
          margin-bottom: 16px;
        }
        .maieute-name {
          font-family: 'Playfair Display', Georgia, serif;
          font-style: italic;
          font-size: 22px;
          color: #FF1744;
          margin: 0 0 4px;
        }
        .maieute-role {
          font-family: ui-monospace, monospace;
          font-size: 10px;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #8a8580;
        }
        .maieute-scroller {
          flex: 1 1 auto;
          overflow-y: auto;
          padding: 8px 4px 16px;
          display: flex;
          flex-direction: column;
          gap: 14px;
          scroll-behavior: smooth;
        }
        .maieute-row { display: flex; }
        .maieute-row.user { justify-content: flex-end; }
        .maieute-row.imani { justify-content: flex-start; }
        .maieute-bubble {
          max-width: 78%;
          padding: 12px 16px;
          border-radius: 14px;
          font-size: 15px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        .maieute-bubble.imani {
          background: rgba(255,23,68, 0.06);
          border: 0.5px solid rgba(255,23,68, 0.28);
          color: #0a0a0a;
          font-family: 'Inter', 'DM Sans', sans-serif;
          font-style: normal;
          font-weight: 400;
          border-bottom-left-radius: 4px;
        }
        .maieute-bubble.imani em { font-style: normal; }
        .maieute-bubble.user {
          background: #0a0a0a;
          color: #ffffff;
          font-family: 'DM Sans', sans-serif;
          border-bottom-right-radius: 4px;
        }
        .maieute-thinking {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 12px 16px;
          background: rgba(255,23,68, 0.06);
          border: 0.5px solid rgba(255,23,68, 0.2);
          border-radius: 14px;
          border-bottom-left-radius: 4px;
          font-family: ui-monospace, monospace;
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #FF1744;
        }
        .maieute-thinking .dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: #FF1744;
          animation: maieute-blink 1.2s ease-in-out infinite;
        }
        .maieute-thinking .dot:nth-child(2) { animation-delay: 0.15s; }
        .maieute-thinking .dot:nth-child(3) { animation-delay: 0.3s; }
        @keyframes maieute-blink {
          0%, 80%, 100% { opacity: 0.25; }
          40% { opacity: 1; }
        }
        .maieute-brief-card {
          margin: 8px 0 16px;
          padding: 20px 22px;
          background: rgba(255,23,68, 0.06);
          border: 0.5px solid #FF1744;
          border-radius: 4px;
        }
        .maieute-brief-intro {
          font-family: 'Playfair Display', Georgia, serif;
          font-style: italic;
          color: #FF1744;
          font-size: 14px;
          margin: 0 0 12px;
        }
        .maieute-brief-text {
          font-family: 'DM Sans', sans-serif;
          color: #0a0a0a;
          font-size: 15px;
          line-height: 1.6;
          white-space: pre-wrap;
          margin: 0 0 18px;
        }
        .maieute-cta {
          font-family: Oswald, sans-serif;
          font-weight: 700;
          font-size: 12px;
          letter-spacing: 3px;
          text-transform: uppercase;
          padding: 14px 24px;
          background: #FF1744;
          color: #ffffff;
          border: 0.5px solid #FF1744;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .maieute-cta:hover:not(:disabled) {
          background: #0a0a0a;
          border-color: #0a0a0a;
          color: #ffffff;
        }
        .maieute-cta:disabled { opacity: 0.5; cursor: wait; }
        .maieute-composer {
          flex: 0 0 auto;
          padding: 14px 0 24px;
          border-top: 0.5px solid rgba(255,23,68, 0.18);
          display: flex;
          gap: 10px;
          align-items: flex-end;
        }
        .maieute-textarea {
          flex: 1;
          resize: none;
          min-height: 44px;
          max-height: 140px;
          padding: 12px 14px;
          background: #ffffff;
          border: 0.5px solid rgba(10, 10, 10, 0.25);
          color: #0a0a0a;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          line-height: 1.45;
          border-radius: 8px;
          outline: none;
        }
        .maieute-textarea:focus { border-color: #FF1744; }
        .maieute-send {
          font-family: Oswald, sans-serif;
          font-weight: 700;
          font-size: 11px;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 12px 18px;
          background: transparent;
          color: #FF1744;
          border: 0.5px solid #FF1744;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .maieute-send:hover:not(:disabled) {
          background: rgba(255,23,68, 0.12);
        }
        .maieute-send:disabled { opacity: 0.35; cursor: not-allowed; }
      `}</style>

      <div className="maieute-header">
        <p className="maieute-name">Imani</p>
        <p className="maieute-role">Contrôleur · Antichambre</p>
      </div>

      <div
        ref={scrollerRef}
        className="maieute-scroller"
        role="log"
        aria-live="polite"
        aria-label="Conversation avec Imani"
      >
        {bubbles.map((b) => (
          <div key={b.id} className={`maieute-row ${b.role}`}>
            <div className={`maieute-bubble ${b.role}`}>{b.text}</div>
          </div>
        ))}

        {thinking && (
          <div className="maieute-row imani">
            <div className="maieute-thinking" aria-label="Imani réfléchit">
              <span>Imani réfléchit</span>
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </div>
          </div>
        )}

        {briefReady && finalBrief && (
          <div className="maieute-brief-card" data-testid="qa-final-brief">
            <p className="maieute-brief-intro">
              Voici comment je présenterais votre question au Conseil :
            </p>
            <p className="maieute-brief-text">{finalBrief}</p>
            <button
              type="button"
              className="maieute-cta"
              onClick={handleConvene}
              disabled={convening}
            >
              {convening ? "Convocation…" : "Lancer le Conseil →"}
            </button>
          </div>
        )}
      </div>

      {!briefReady && (
        <form className="maieute-composer" onSubmit={handleSend}>
          <ConversationAttachmentButton
            conversationId={sessionId}
            initialCount={initialAttachmentCount}
            disabled={thinking}
          />
          <textarea
            ref={textareaRef}
            className="maieute-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void handleSend(e as unknown as React.FormEvent);
              }
            }}
            placeholder="Votre réponse à Imani…"
            disabled={thinking}
            rows={1}
            autoFocus
            aria-label="Message à Imani"
          />
          <button
            type="submit"
            className="maieute-send"
            disabled={thinking || !input.trim()}
          >
            Envoyer
          </button>
        </form>
      )}
    </div>
  );
}

export function isAuthError(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}
